import { WorkflowSection } from '@ebalance/proto/bffuser/v1/workflow_template_model_pb'

import { defineFilterCodec, nonEmptyText, setOf } from '~/models/filterCodec'
import { workflowSectionFilter } from '~/models/workflowSectionFilter'

/**
 * ワークフロー雛形一覧のフィルタバー保持 key。
 *
 * AC-SC-031 [P0] (`docs/specs/common/shared-components.md`) は「フィルタバーを持つ
 * 全画面において、フィルタの選択状態を localStorage で永続化し、他画面への遷移後・
 * 再訪時に前回の選択状態を復元する」と保存媒体まで規定する。
 */
export const ADMIN_WORKFLOW_TEMPLATES_FILTERS_STORAGE_KEY =
  'ebalance-admin-workflow-templates-filters'

/**
 * 保持対象のフィルタ state。
 *
 * ## 保持しないもの
 *
 * - **`viewMode`** — 一覧 / 詳細 / 編集の切替。フィルタではなく画面モードで、
 *   しかも同一 route 内で切り替わる (page は unmount しない)。再訪時に編集画面が
 *   開いた状態で始まるのは明らかに誤り。
 * - **削除ダイアログ・削除エラー** — 一時的な UI 状態。
 * - **ドラッグ中の行 / ドロップ位置** — 操作中だけの一時状態。
 */
export type AdminWorkflowTemplatesFilters = {
  /** 業務区分フィルタ。`null` = すべて。 */
  filterSection: WorkflowSection | null
  /** タグフィルタ (複数選択)。空 = すべて。 */
  selectedTagLabels: Set<string>
}

/**
 * 計画断面フィルタが画面に出す 4 値。旧当日系の保存値は単日に復元する。
 *
 * **この定数が UI の選択肢と復元の妥当集合の両方の SoT。** `index.tsx` の
 * `TEMPLATE_FILTER_SECTION_ITEMS` もここから作る。
 *
 * proto enum 全体を妥当集合にすると、**画面に無い断面 (週次等) を復元してしまい、
 * segmented control がどれも選択されていない状態になって解除もできない**
 * (review 指摘 #6969)。逆に手で少なく列挙すると「選べるのに復元で捨てる」
 * (review 指摘 #6945)。**UI の選択肢と同じ 1 つの列を両方に使う**のが正解。
 */
export const FILTER_VISIBLE_SECTIONS: readonly WorkflowSection[] = [
  WorkflowSection.SINGLE_DAY,
  WorkflowSection.WEEKLY,
  WorkflowSection.MONTHLY,
  WorkflowSection.ANNUAL,
]

const filterSection = workflowSectionFilter(FILTER_VISIBLE_SECTIONS)

/** フィルタ既定値 = 絞り込みなし。時計に依存しない。 */
export function defaultAdminWorkflowTemplatesFilters(): AdminWorkflowTemplatesFilters {
  return { filterSection: null, selectedTagLabels: new Set() }
}

/**
 * 保存形式は `{ filterSection: number | null, selectedTagLabels: string[] }`。
 * 壊れた値・未知 enum は項目単位で既定へ落ちる。
 *
 * **タグはここでは候補表と突き合わせない** (候補はサーバー由来で純関数からは
 * 見えない)。ただし**放置すると詰む**: `DsMultiSelect` は現行 options に含まれる値しか
 * チップに出さないため、候補から消えたタグが復元されると**一覧は 0 件なのに選択が
 * 画面に見えず解除もできない** (review 指摘 #6969)。
 *
 * 候補が確定した後に [[pruneUnavailableTags]] で落とすのは呼び出し側の責務。
 */
export const adminWorkflowTemplatesFiltersCodec =
  defineFilterCodec<AdminWorkflowTemplatesFilters>({
    defaults: defaultAdminWorkflowTemplatesFilters,
    fields: {
      filterSection,
      selectedTagLabels: setOf(nonEmptyText()),
    },
  })

/**
 * 現行のタグ候補に無い選択を落とす。
 *
 * `DsMultiSelect` は options に無い値をチップに出さないので、候補から消えたタグを
 * 抱えたままにすると「一覧は 0 件なのに解除できない」行き止まりになる
 * (review 指摘 #6969)。
 *
 * **候補が確定してから呼ぶこと。** 取得前は候補が空 (またはプリセットのみ) なので、
 * 早すぎるタイミングで呼ぶと正当な選択まで消える (特異日マスタ #6910・48点データ
 * #6932 で踏んだのと同じ「取得完了ガード」の話)。
 *
 * 変化が無ければ**同じ参照を返す**。呼び出し側が参照同一性で「変わっていない」を
 * 判定でき、無駄な保存を誘発しない。
 */
export function pruneUnavailableTags(
  selected: Set<string>,
  availableTagLabels: readonly string[],
): Set<string> {
  if (selected.size === 0) return selected
  const available = new Set(availableTagLabels)
  const kept = [...selected].filter((label) => available.has(label))
  return kept.length === selected.size ? selected : new Set(kept)
}
